import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit,
  HostListener,
  ContentChildren,
  QueryList,
  AfterContentInit,
  Directive,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';

@Directive({
  selector: 'ng-option',
})
export class NgOptionDirective {
  @Input() value: any;
  constructor(public el: ElementRef<HTMLElement>) {}
}

@Component({
  selector: 'app-custom-multi-select',
  templateUrl: './custom-multi-select.component.html',
  styleUrls: ['./custom-multi-select.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomMultiSelectComponent),
      multi: true,
    },
  ],
})
export class CustomMultiSelectComponent
  implements ControlValueAccessor, OnInit, AfterContentInit
{
  @ContentChildren(NgOptionDirective) projectedOptions!: QueryList<NgOptionDirective>;
  @ViewChild('searchInput', { static: false }) searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('tagContainer', { static: false }) tagContainer?: ElementRef<HTMLElement>;

  @Input() options: any[] = [];
  @Input() bindLabel: string = 'label';
  @Input() bindValue: string = 'value';
  @Input() placeholder: string = '';
  @Input() multiple: boolean = false;
  @Input() searchable: boolean = true;
  @Input() translateLabels: boolean = false;
  @Input() disabled: boolean = false;
  @Input() clearable: boolean = true;
  @Input() appendTo: any;
  @Input() hideSelected: boolean = false;
  @Input() set items(value: any[]) {
    this.options = value || [];
  }
  @Input() set readOnly(val: boolean) {
    this.setDisabledState(val);
  }

  selected: any[] = [];
  searchTerm = '';
  showDropdown = false;

  onChange = (_: any) => {};
  onTouched = () => {};

  constructor(private translate: TranslateService) {}

  ngOnInit() {
    console.log(this.placeholder)
  }

  ngAfterContentInit(): void {
    this.syncProjectedOptions();
    this.projectedOptions?.changes.subscribe(() => this.syncProjectedOptions());
  }

  get filteredOptions(): any[] {
    const term = this.searchTerm.toLowerCase();
    return this.options
      .filter((opt) => !this.isSelected(this.getValue(opt)))
      .filter((opt) => {
        if (!this.searchable || !term) {
          return true;
        }
        return this.getLabel(opt).toLowerCase().includes(term);
      });
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (this.disabled) {
      return;
    }
    const clickedInside = (event.target as HTMLElement).closest(
      '.custom-multi-select'
    );
    if (!clickedInside) {
      this.showDropdown = false;
    }
  }

  toggleDropdown() {
    if (this.disabled) {
      return;
    }
    this.showDropdown = !this.showDropdown;
  }

  get isOpen() {
    return this.showDropdown;
  }

  open() {
    if (this.disabled) {
      return;
    }
    this.showDropdown = true;
  }

  close() {
    this.showDropdown = false;
  }

  selectOption(option: any) {
    if (this.disabled) {
      return;
    }
    const value = this.getValue(option);
    if (this.multiple) {
      this.selected = [...this.selected, value];
    } else {
      this.selected = [value];
    }
    this.searchTerm = '';
    this.showDropdown = false;
    this.propagateChange();
  }

  removeOption(option: any) {
    if (this.disabled) {
      return;
    }
    this.selected = this.selected.filter((o) => o !== option);
    this.propagateChange();
  }

  clearModel() {
    this.selected = [];
    this.searchTerm = '';
    this.showDropdown = false;
    this.propagateChange();
    this.onTouched();
  }

  scrollToEnd() {
    const container = this.tagContainer?.nativeElement;
    if (!container) {
      return;
    }
    const tags = container.querySelectorAll<HTMLElement>('.tag');
    const target = tags.length ? tags[tags.length - 1] : null;
    target?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  onSingleChange(value: any) {
    this.selected = value !== null && value !== undefined ? [value] : [];
    this.propagateChange();
    this.onTouched();
  }

  writeValue(obj: any): void {
    if (this.multiple) {
      this.selected = Array.isArray(obj) ? [...obj] : [];
    } else {
      this.selected = obj !== null && obj !== undefined ? [obj] : [];
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.showDropdown = false;
      this.searchTerm = '';
    }
  }

  isSelected(value: any): boolean {
    return this.selected.includes(value);
  }

  getLabel(opt: any): string {
    if (opt && typeof opt === 'object') {
      const value = opt[this.bindLabel];
      const label =
        value !== undefined && value !== null ? String(value) : '';
      return this.translateLabels ? this.translate.instant(label) : label;
    }
    const label = opt !== undefined && opt !== null ? String(opt) : '';
    return this.translateLabels ? this.translate.instant(label) : label;
  }

  getValue(opt: any): any {
    if (opt && typeof opt === 'object') {
      return opt[this.bindValue];
    }
    return opt;
  }

  getLabelForValue(value: any): string {
    const found = this.options.find((opt) => this.getValue(opt) === value);
    return found ? this.getLabel(found) : (value ?? '').toString();
  }

  private propagateChange() {
    const value = this.multiple ? [...this.selected] : this.selected[0] ?? null;
    this.onChange(value);
  }

  private syncProjectedOptions() {
    // Only use projected options when an explicit options input is not provided.
    if (this.options && this.options.length) {
      return;
    }
    if (this.projectedOptions && this.projectedOptions.length) {
      this.options = this.projectedOptions.map((opt) => {
        const label = (opt.el.nativeElement.textContent || '').trim();
        const value = opt.value !== undefined ? opt.value : label;
        return { [this.bindLabel]: label, [this.bindValue]: value };
      });
    }
  }
}
